import { useRef, useState, useEffect } from "react";

const formatValue = (value) => {
  if (new RegExp(/^\d{2}:\d{2}$/).test(value)) return value;
  if (typeof value === "string") {
    const timeMatch = value.match(/(\d{1,2}):(\d{1,2})/);
    if (timeMatch === true) {
      const hours = timeMatch[1].padStart(2, "0");
      const minutes = timeMatch[2].padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  }
  return "";
};

const TimeInput = ({ value, onChange, placeholder = "", className = "", isInError = false, ...rest }) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(formatValue(value));

  useEffect(() => {
    if (value === inputValue || !value) return;
    setInputValue(formatValue(value));
  }, [value]);

  const handleChange = (event) => {
    const inputElement = event.target;
    const rawValue = inputElement.value;
    if (typeof rawValue !== "string") return;

    const selectionStart = typeof inputElement.selectionStart === "number" ? inputElement.selectionStart : rawValue.length;
    const rawCharacters = rawValue.split("");
    const sanitizedCharacters = [];
    const sanitizedIndices = new Array(rawCharacters.length + 1);

    let digitCount = 0;
    let colonIndex = undefined;
    for (let index = 0; index < rawCharacters.length; index += 1) {
      sanitizedIndices[index] = sanitizedCharacters.length;
      const character = rawCharacters[index];
      const isDigitCharacter = character >= "0" && character <= "9";
      if (isDigitCharacter === true) {
        if (digitCount === 4) continue;
        sanitizedCharacters.push(character);
        digitCount += 1;
        continue;
      }
      if (character === ":" && index === 2) {
        if (colonIndex !== undefined) continue;
        sanitizedCharacters.push(character);
        colonIndex = index;
      }
    }
    sanitizedIndices[rawCharacters.length] = sanitizedCharacters.length;

    const sanitizedValue = sanitizedCharacters.join("");
    const digitsOnlyValue = sanitizedValue.replace(/\D/g, "");
    const hoursSegment = digitsOnlyValue.slice(0, 2);
    const minutesSegment = digitsOnlyValue.slice(2, 4);
    const shouldIncludeColon = digitsOnlyValue.length > 2 || colonIndex !== undefined;

    let formattedValue = "";
    if (hoursSegment.length > 0) formattedValue += hoursSegment;
    if (shouldIncludeColon === true) formattedValue += ":";
    if (minutesSegment.length > 0) formattedValue += minutesSegment;

    const sanitizedCaretPosition = sanitizedIndices[selectionStart];
    const sanitizedBeforeCaret = sanitizedValue.slice(0, sanitizedCaretPosition);
    const digitsBeforeCaret = sanitizedBeforeCaret.replace(/\D/g, "").length;
    const hasColonBeforeCaret = sanitizedBeforeCaret.indexOf(":") !== -1;

    let nextCaretPosition = digitsBeforeCaret;
    if (shouldIncludeColon === true && (digitsBeforeCaret > 2 || hasColonBeforeCaret === true)) nextCaretPosition += 1;
    if (nextCaretPosition > formattedValue.length) nextCaretPosition = formattedValue.length;

    setInputValue(formattedValue);

    setTimeout(() => {
      if (inputRef.current === null) return;
      inputRef.current.setSelectionRange(nextCaretPosition, nextCaretPosition);
    }, 0);

    if (formattedValue.length !== 5) return onChange(null);

    const hours = parseInt(hoursSegment, 10);
    const minutes = parseInt(minutesSegment, 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      onChange(formattedValue);
      return;
    }
  };

  return (
    <div className="relative">
      <input
        id={rest.id}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        maxLength={5}
        placeholder={"HH:MM"}
        className={`${className} ${isInError ? "border-red-500" : "border-gray-300"}
          block w-full px-3 py-2 bg-white border rounded-md focus:outline-none focus:ring-primary focus:border-primary
          peer disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 h-[36px] `}
        value={inputValue}
        onChange={handleChange}
        {...rest}
      />
      {placeholder && (
        <label
          htmlFor={rest.id}
          className={`absolute left-2 -top-2.5 px-1 text-xs transition-all
            peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
            peer-placeholder-shown:top-2 peer-focus:-top-2.5 peer-focus:text-xs
          peer-focus:text-primary bg-white ${isInError ? "text-red-500" : "text-gray-600"}
            pointer-events-none`}
        >
          {placeholder}
        </label>
      )}
    </div>
  );
};

export default TimeInput;
